import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Phone, X, Search } from 'lucide-react';
import { Client } from '../types';
import { clientRecordFromChartResponse, clientDisplayName, entryClientRecord, presentCallText } from '../utils/clientDetailRequest';
import { playPulseSeal } from '../utils/pulseAudio';
import {
  UniversalCallPurpose,
  UniversalCallPacket,
  UNIVERSAL_CALL_PURPOSES,
  PINNED_RETELL_OFFICE_AGENT_VERSION
} from '../types/universalCall';
import {
  buildInitialCallPacket,
  isReviewableDestination,
  reviewedDestinationNumber
} from '../utils/universalCall';

export interface UniversalCallModalProps {
  initialClient?: Client | any | null;
  allClients?: Client[];
  currentUser?: { id: string; name?: string; email?: string; role?: string } | null;
  onClose: () => void;
  onSelectClient?: (clientId: string) => void;
}

const inputClass = 'w-full bg-slate-50/70 border border-slate-200 rounded-lg px-3 py-2 sm:py-1.5 text-base sm:text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-sky-500';
const labelClass = 'block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1';

export const UniversalCallModal: React.FC<UniversalCallModalProps> = ({
  initialClient = null,
  allClients = [],
  onClose
}) => {
  const [selectedClient, setSelectedClient] = useState<any | null>(() => entryClientRecord(initialClient));
  const activeClientIdRef = useRef<string | null | undefined>(undefined);
  if (activeClientIdRef.current === undefined) {
    activeClientIdRef.current = selectedClient?.id ? String(selectedClient.id) : null;
  }
  const [clientsList, setClientsList] = useState<any[]>(allClients);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientSearch, setShowClientSearch] = useState(() => !entryClientRecord(initialClient)?.id);
  const [loadingClients, setLoadingClients] = useState(false);
  const [purpose, setPurpose] = useState<UniversalCallPurpose>('VERIFY_MEMBER_ACTIVE_FAX');
  const purposeRef = useRef(purpose);
  purposeRef.current = purpose;
  const [clientDocs, setClientDocs] = useState<any[]>([]);
  const [clientFaxes, setClientFaxes] = useState<any[]>([]);
  const [loadingContext, setLoadingContext] = useState(false);
  const [packet, setPacket] = useState<UniversalCallPacket | null>(() =>
    buildInitialCallPacket(entryClientRecord(initialClient), 'VERIFY_MEMBER_ACTIVE_FAX', [], [])
  );
  const [telephonyInfo, setTelephonyInfo] = useState<{
    outboundCallingEnabled: boolean;
    boundaryStatus: string;
    reason: string;
    normalizedFromNumber?: string;
  } | null>(null);
  const [isCalling, setIsCalling] = useState(false);
  const [callResult, setCallResult] = useState<any | null>(null);
  const [callError, setCallError] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/universal-call/info')
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data) setTelephonyInfo(data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (clientsList.length > 0) return;
    setLoadingClients(true);
    fetch('/api/clients')
      .then(res => res.json())
      .then(data => { if (Array.isArray(data)) setClientsList(data); })
      .catch(err => console.warn('Could not load client census for Universal Call:', err))
      .finally(() => setLoadingClients(false));
  }, [clientsList.length]);

  useEffect(() => {
    if (!selectedClient?.id) {
      setClientDocs([]);
      setClientFaxes([]);
      return;
    }
    const requestedId = String(selectedClient.id);
    setLoadingContext(true);
    Promise.all([
      fetch(`/api/clinical/docs?client_id=${selectedClient.id}`).then(r => r.ok ? r.json() : []).catch(() => []),
      fetch('/api/communications/faxes').then(r => r.ok ? r.json() : []).catch(() => []),
      fetch(`/api/clients/${selectedClient.id}`).then(r => r.ok ? r.json() : selectedClient).catch(() => selectedClient)
    ])
      .then(([docs, faxes, fullClient]) => {
        if (activeClientIdRef.current !== requestedId) return;
        const resolvedClient = clientRecordFromChartResponse(fullClient, selectedClient);
        if (!resolvedClient?.id || String(resolvedClient.id) !== requestedId) return;
        const docList = Array.isArray(docs) ? docs : [];
        const faxList = Array.isArray(faxes) ? faxes : [];
        setSelectedClient(resolvedClient);
        setClientDocs(docList);
        setClientFaxes(faxList);
        setPacket(current => {
          const loaded = buildInitialCallPacket(resolvedClient, purposeRef.current, docList, faxList);
          if (!current?.edited_fields) return loaded;
          const next: UniversalCallPacket = { ...loaded, edited_fields: { ...current.edited_fields } };
          for (const [field, edited] of Object.entries(current.edited_fields)) {
            if (edited) (next as any)[field] = (current as any)[field];
          }
          return next;
        });
      })
      .finally(() => setLoadingContext(false));
  }, [selectedClient?.id]);

  const destination = reviewedDestinationNumber(packet);
  const destinationReady = isReviewableDestination(destination);
  const clientName = clientDisplayName(selectedClient);

  const filteredClients = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    const rows = q
      ? clientsList.filter(c =>
        `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase().includes(q) ||
        `${c.last_name || ''} ${c.first_name || ''}`.toLowerCase().includes(q) ||
        String(c.id || '').toLowerCase().includes(q)
      )
      : clientsList;
    return rows.slice(0, 20);
  }, [clientsList, clientSearch]);

  const handlePurposeChange = (newPurpose: UniversalCallPurpose) => {
    setPurpose(newPurpose);
    setPacket(current => {
      const loaded = buildInitialCallPacket(selectedClient, newPurpose, clientDocs, clientFaxes);
      if (!current?.edited_fields) return loaded;
      const next: UniversalCallPacket = { ...loaded, edited_fields: { ...current.edited_fields } };
      for (const [field, edited] of Object.entries(current.edited_fields)) {
        if (edited) (next as any)[field] = (current as any)[field];
      }
      return next;
    });
  };

  const handlePacketChange = (field: keyof UniversalCallPacket, value: string) => {
    setPacket(prev => {
      if (!prev) return prev;
      const stored = value.trim() ? value : 'Unavailable';
      const next: UniversalCallPacket = {
        ...prev,
        [field]: stored,
        edited_fields: { ...prev.edited_fields, [field]: true }
      };
      if (field === 'practice_name') {
        next.target_name = stored;
        next.physician_name = stored;
        next.edited_fields.target_name = true;
        next.edited_fields.physician_name = true;
      }
      if (field === 'department') {
        next.target_department = stored;
        next.edited_fields.target_department = true;
      }
      if (field === 'member_dob') {
        next.dob = stored;
        next.edited_fields.dob = true;
      }
      if (field === 'address') {
        next.member_address = stored;
        next.edited_fields.member_address = true;
      }
      if (field === 'active_fax_number') {
        next.office_fax_on_file = stored;
        next.edited_fields.office_fax_on_file = true;
      }
      if (field === 'poc_document_title') {
        next.poc_title = stored;
        next.edited_fields.poc_title = true;
      }
      if (field === 'poc_document_id') {
        next.poc_identifier = stored;
        next.edited_fields.poc_identifier = true;
      }
      if (field === 'poc_fax_sent_at') {
        next.poc_date_sent = stored;
        next.fax_sent_at = stored;
        next.edited_fields.fax_sent_at = true;
      }
      if (field === 'poc_fax_destination') {
        next.fax_sent_to = stored;
        next.edited_fields.fax_sent_to = true;
      }
      if (field === 'recent_fax_document_type') {
        next.routing_instructions = stored;
        next.edited_fields.routing_instructions = true;
      }
      return next;
    });
  };

  const chooseClient = (client: any) => {
    const record = entryClientRecord(client);
    if (!record?.id) return;
    activeClientIdRef.current = String(record.id);
    setSelectedClient(record);
    setShowClientSearch(false);
    setClientSearch('');
    setCallResult(null);
    setCallError(null);
  };

  const clearClient = () => {
    activeClientIdRef.current = null;
    setSelectedClient(null);
    setClientDocs([]);
    setClientFaxes([]);
    setShowClientSearch(true);
    setClientSearch('');
  };

  const handleInitiateCall = async () => {
    if (!packet) return;
    const reviewedTargetPhone = reviewedDestinationNumber(packet);
    if (!isReviewableDestination(reviewedTargetPhone)) {
      setCallError('Enter a destination phone number with at least 10 digits.');
      return;
    }
    const who = clientName ? ` for ${clientName}` : '';
    if (!window.confirm(`Place a call to ${reviewedTargetPhone}${who}? This starts the call now.`)) return;

    setIsCalling(true);
    setCallResult(null);
    setCallError(null);
    try {
      const mintRes = await fetch('/api/universal-call/mint-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId: selectedClient?.id || null,
          purpose,
          reviewedTargetPhone,
          packet
        })
      });
      if (!mintRes.ok) {
        const err = await mintRes.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to mint call intent');
      }
      const { intent } = await mintRes.json();
      const dispatchRes = await fetch('/api/universal-call/dispatch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ intentId: intent.id })
      });
      const dispatchData = await dispatchRes.json().catch(() => ({}));
      if (!dispatchRes.ok) throw new Error(dispatchData.error || 'Failed to dispatch call');
      if (dispatchData.success) playPulseSeal();
      setCallResult(dispatchData);
    } catch (err: any) {
      setCallError(err.message || 'Call initiation failed');
    } finally {
      setIsCalling(false);
    }
  };

  const placeCallDisabled = isCalling || !destinationReady || !telephonyInfo?.outboundCallingEnabled;
  const faxUnverified = purpose === 'CHECK_POC' && packet?.poc_fax_evidence_link === 'UNVERIFIED_CANDIDATE';

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-xs flex flex-col sm:items-center sm:justify-center p-0 sm:p-4 animate-in fade-in duration-150 overflow-y-auto">
      <div className="relative w-full h-full sm:h-auto max-w-full sm:max-w-xl rounded-none sm:rounded-2xl bg-white border-0 sm:border border-slate-200/90 shadow-xl overflow-hidden max-h-full sm:max-h-[90vh] flex flex-col">
        <div className="px-4 sm:px-6 py-3.5 sm:py-4 bg-slate-900 border-b border-slate-800 flex items-center justify-between shrink-0">
          <div className="flex items-center space-x-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-200 shrink-0">
              <Phone className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-bold text-white tracking-tight truncate">Universal Call</h3>
              <p className="text-[11px] text-slate-300 truncate">
                {clientName || 'No client selected'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors shrink-0"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 sm:p-5 space-y-3.5 text-xs overflow-y-auto flex-1">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className={labelClass}>Client</label>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowClientSearch(open => !open)}
                  className="text-[10px] text-slate-500 underline"
                >
                  {showClientSearch ? 'Hide search' : 'Find client'}
                </button>
                {selectedClient?.id && (
                  <button type="button" onClick={clearClient} className="text-[10px] text-slate-500 underline">
                    Clear client
                  </button>
                )}
              </div>
            </div>
            {selectedClient?.id && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-slate-800 font-medium">
                {clientName || presentCallText(selectedClient.id)}
                {presentCallText(selectedClient.id) ? <span className="ml-2 font-mono text-[10px] text-slate-500">{presentCallText(selectedClient.id)}</span> : null}
              </div>
            )}
            {showClientSearch && (
              <div className="mt-2 border border-slate-200 rounded-lg overflow-hidden">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-2.5" />
                  <input
                    type="text"
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    placeholder="Search by client name..."
                    className="w-full bg-white border-b border-slate-200 pl-8 pr-3 py-2 text-xs focus:outline-none"
                  />
                </div>
                <div className="max-h-36 overflow-y-auto">
                  {loadingClients && filteredClients.length === 0 ? (
                    <div className="px-3 py-2 text-slate-500">Loading clients...</div>
                  ) : filteredClients.length === 0 ? (
                    <div className="px-3 py-2 text-slate-500">No matching clients</div>
                  ) : filteredClients.map(client => (
                    <button
                      key={client.id}
                      type="button"
                      onClick={() => chooseClient(client)}
                      className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0"
                    >
                      {clientDisplayName(client) || presentCallText(client.id)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div>
            <label className={labelClass}>Call purpose</label>
            <select
              value={purpose}
              onChange={(e) => handlePurposeChange(e.target.value as UniversalCallPurpose)}
              className={`${inputClass} font-medium`}
            >
              <option value="VERIFY_MEMBER_ACTIVE_FAX">{UNIVERSAL_CALL_PURPOSES.VERIFY_MEMBER_ACTIVE_FAX.tagline}</option>
              <option value="CHECK_POC">{UNIVERSAL_CALL_PURPOSES.CHECK_POC.tagline}</option>
            </select>
            <p className="text-[10px] text-slate-500 mt-1">{UNIVERSAL_CALL_PURPOSES[purpose].description}</p>
          </div>

          <div>
            <label className={labelClass}>Physician / office</label>
            <input
              type="text"
              value={presentCallText(packet?.practice_name)}
              onChange={(e) => handlePacketChange('practice_name', e.target.value)}
              placeholder="Office or physician name"
              className={inputClass}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className={labelClass}>Member first name</label>
              <input type="text" value={presentCallText(packet?.member_first_name)} onChange={(e) => handlePacketChange('member_first_name', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Member last name</label>
              <input type="text" value={presentCallText(packet?.member_last_name)} onChange={(e) => handlePacketChange('member_last_name', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Date of birth</label>
              <input type="text" value={presentCallText(packet?.member_dob)} onChange={(e) => handlePacketChange('member_dob', e.target.value)} placeholder="YYYY-MM-DD" className={`${inputClass} font-mono`} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className={labelClass}>Member phone</label>
              <input type="text" value={presentCallText(packet?.member_phone)} onChange={(e) => handlePacketChange('member_phone', e.target.value)} placeholder="Not the number being called" className={`${inputClass} font-mono`} />
            </div>
            <div>
              <label className={labelClass}>Guardian</label>
              <input type="text" value={presentCallText(packet?.guardian_name)} onChange={(e) => handlePacketChange('guardian_name', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Guardian phone</label>
              <input type="text" value={presentCallText(packet?.guardian_phone)} onChange={(e) => handlePacketChange('guardian_phone', e.target.value)} className={`${inputClass} font-mono`} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Address</label>
              <input type="text" value={presentCallText(packet?.address)} onChange={(e) => handlePacketChange('address', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Return fax</label>
              <input type="text" value={presentCallText(packet?.allegiant_return_fax)} onChange={(e) => handlePacketChange('allegiant_return_fax', e.target.value)} placeholder="No return fax configured" className={`${inputClass} font-mono`} />
            </div>
          </div>

          <div>
            <label className={labelClass}>Department</label>
            <input
              type="text"
              value={presentCallText(packet?.department)}
              onChange={(e) => handlePacketChange('department', e.target.value)}
              placeholder="Medical records or front desk"
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Destination phone</label>
            <input
              type="tel"
              value={destination}
              onChange={(e) => handlePacketChange('target_phone', e.target.value)}
              placeholder="Enter the office number to call"
              className={`${inputClass} font-mono`}
            />
            {!destinationReady && (
              <p className="text-[10px] text-slate-500 mt-1">A destination number is required before Place Call. A missing chart phone does not block the rest of this form.</p>
            )}
          </div>

          {purpose === 'VERIFY_MEMBER_ACTIVE_FAX' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Fax on file</label>
                <input
                  type="text"
                  value={presentCallText(packet?.active_fax_number)}
                  onChange={(e) => handlePacketChange('active_fax_number', e.target.value)}
                  placeholder="No fax number on the chart"
                  className={`${inputClass} font-mono`}
                />
              </div>
              <div>
                <label className={labelClass}>Recent fax date</label>
                <input
                  type="text"
                  value={presentCallText(packet?.recent_fax_date)}
                  onChange={(e) => handlePacketChange('recent_fax_date', e.target.value)}
                  placeholder="No fax date on file"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Recent fax document</label>
                <input
                  type="text"
                  value={presentCallText(packet?.recent_fax_document_type)}
                  onChange={(e) => handlePacketChange('recent_fax_document_type', e.target.value)}
                  placeholder="No linked fax document"
                  className={inputClass}
                />
              </div>
            </div>
          )}

          {purpose === 'CHECK_POC' && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Plan of care</label>
                  <input
                    type="text"
                    value={presentCallText(packet?.poc_document_title)}
                    onChange={(e) => handlePacketChange('poc_document_title', e.target.value)}
                    placeholder="No plan of care on the chart"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Document</label>
                  <input
                    type="text"
                    value={presentCallText(packet?.poc_document_id)}
                    onChange={(e) => handlePacketChange('poc_document_id', e.target.value)}
                    placeholder="No document id"
                    className={`${inputClass} font-mono`}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Fax sent</label>
                  <input
                    type="text"
                    value={presentCallText(packet?.poc_fax_sent_at || packet?.poc_date_sent)}
                    onChange={(e) => handlePacketChange('poc_fax_sent_at', e.target.value)}
                    placeholder="No verified send date"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Fax destination</label>
                  <input
                    type="text"
                    value={presentCallText(packet?.poc_fax_destination)}
                    onChange={(e) => handlePacketChange('poc_fax_destination', e.target.value)}
                    placeholder="No verified fax destination"
                    className={`${inputClass} font-mono`}
                  />
                </div>
              </div>
              {faxUnverified && packet?.poc_fax_candidate_note && (
                <p className="text-[10px] text-slate-500">{packet.poc_fax_candidate_note}</p>
              )}
            </div>
          )}

          <div>
            <label className={labelClass}>Instructions</label>
            <textarea
              rows={3}
              value={presentCallText(packet?.staff_instructions)}
              onChange={(e) => handlePacketChange('staff_instructions', e.target.value)}
              className={inputClass}
            />
          </div>

          <p className="text-[10px] text-slate-500">
            When a client is associated, Bloom records this call on that client's Notes tab after Retell accepts it, then updates that same note when the call ends or the analysis arrives. A call with no client is not attached to a chart. The plan and clinical documents are not changed.
            {loadingContext ? ' Loading chart details.' : ''}
          </p>

          {!telephonyInfo?.outboundCallingEnabled && (
            <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-amber-900 text-[11px]">
              {telephonyInfo?.reason || 'Outbound calling stays off until Retell is configured. You can still review and complete these fields.'}
            </div>
          )}
          {callError && (
            <div role="alert" className="p-3 bg-rose-50 border border-rose-200 rounded-lg text-rose-800 text-xs">{callError}</div>
          )}
          {callResult?.success && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-900 text-xs">
              Call accepted. The note is on the client chart.
            </div>
          )}

          <div className="flex justify-end space-x-2.5 pt-3 border-t border-slate-200 shrink-0 pb-[calc(16px+env(safe-area-inset-bottom,0px))] sm:pb-0">
            <button
              type="button"
              onClick={onClose}
              className="px-3.5 py-2 rounded-lg bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 font-semibold transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleInitiateCall}
              disabled={placeCallDisabled}
              title={!telephonyInfo?.outboundCallingEnabled
                ? (telephonyInfo?.reason || 'Telephony is not configured')
                : !destinationReady
                  ? 'Enter a destination phone number'
                  : `Agent v${PINNED_RETELL_OFFICE_AGENT_VERSION}`}
              className="px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-semibold disabled:opacity-50 shadow-xs"
            >
              {isCalling ? 'Placing Call...' : 'Place Call'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
